// NOMBASE — Gateway URL resolver (runtime-derived fallback).
//
// History
// -------
// CRA bakes `REACT_APP_*` into the bundle at BUILD time. The MRP prod
// build pipeline repeatedly produced a bundle where
// `REACT_APP_GATEWAY_URL` was baked as the MRP origin itself
// (`https://mrp.nombaseconnect.com`) — same value as
// `REACT_APP_BACKEND_URL` — regardless of what was set in the
// Custom Keys panel. The companion Gateway app deployed on the same
// platform does NOT exhibit this (its bundle has both Gateway and
// MRP URLs correctly baked), so the bug is MRP-specific: most likely
// the Custom Keys panel save was not persisting that variable to the
// build pipeline (UI display vs persisted-value drift). craco.config
// is clean — no DefinePlugin, no env mangling.
//
// We cannot fix the Secrets-panel persistence from inside the
// pod. We CAN derive the Gateway URL deterministically at runtime
// from `window.location` when the build-time env is unset or
// self-pointing — the deployment topology is a static convention
// (MRP lives at `mrp.{root}`, Gateway lives at `{root}`), so this
// derivation is safe.
//
// Resolution order (first non-null wins):
//   1. `REACT_APP_GATEWAY_URL` env var IF non-empty AND a valid
//      absolute URL AND its origin != current `window.location.origin`.
//      Preview pods use this branch (preview pods).
//   2. Derived from `window.location`: if the current host starts with
//      `mrp.`, strip that prefix and use the remainder as the Gateway
//      host on the same scheme. This is the prod self-heal.
//   3. `null` — caller must handle (the existing call sites already
//      do — they log and short-circuit the redirect).
//
// We intentionally do NOT default to a hardcoded
// `https://nombaseconnect.com` for two reasons: (a) it would mask
// the misconfig in non-prod environments, and (b) it would block
// any future preview hostname that doesn't follow the `mrp.` prefix
// convention.
//
// Used by:
//   • `AuthContext.navigateToSignIn` — sign-out + re-auth bounce.
//   • `App.js` unauthenticated `useEffect` — first-visit redirect.

const _trim = (s) => (s || '').replace(/\/+$/, '');

/**
 * Resolve the Nombase Gateway origin.
 *
 * @returns {{url: string, source: 'env' | 'derived' | 'none'}}
 *   `url` — Gateway origin WITHOUT trailing slash (e.g.
 *           `https://nombaseconnect.com`). Empty string when source is
 *           `'none'` so callers can keep their existing
 *           `if (!gatewayUrl)` guards.
 *   `source` — diagnostic-only; reported in console errors so the
 *              next operator can tell whether the env var landed or
 *              the runtime fallback fired.
 */
export function resolveGatewayUrl() {
  if (typeof window === 'undefined') return { url: '', source: 'none' };

  const raw = _trim(process.env.REACT_APP_GATEWAY_URL);
  if (raw) {
    try {
      const envOrigin = new URL(raw).origin;
      if (envOrigin !== window.location.origin) {
        return { url: envOrigin, source: 'env' };
      }
      // Same-origin → fall through to runtime derivation.
    } catch (_) {
      // Malformed URL → fall through to runtime derivation.
    }
  }

  // Runtime derivation from the current host. Strip a leading `mrp.`
  // subdomain to land on the Gateway host. Any other host shape
  // (e.g. preview pods, localhost) returns
  // `'none'` so the caller's existing diagnostics fire.
  try {
    const here = window.location;
    if (here.host && here.host.startsWith('mrp.')) {
      const gwHost = here.host.slice(4); // drop "mrp."
      return { url: `${here.protocol}//${gwHost}`, source: 'derived' };
    }
  } catch (_) {
    // window.location access failed (shouldn't happen in browsers).
  }

  return { url: '', source: 'none' };
}
